import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { X, Trophy, TrendingUp, Award, Target } from 'lucide-react';

const PRNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    if (!user?.email) {
      return;
    }

    const userEmail = user.email;

    // Function to load unnotified PRs - defined inside useEffect to access userEmail
    const loadUnnotifiedPRs = async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_unnotified_prs', { p_member_email: userEmail });

        if (error) throw error;

        // Filter to show only max_reps PRs for now
        const newNotifications = (data || []).filter(pr => pr.pr_type === 'max_reps');
        setNotifications(newNotifications);

        // Auto-dismiss each notification after 8 seconds
        newNotifications.forEach(notification => {
          setTimeout(() => {
            dismissNotification(notification.id);
          }, 8000);
        });
      } catch (error) {
        console.error('Error loading PR notifications:', error);
      }
    };

    // Load initial PRs
    loadUnnotifiedPRs();

    // Set up real-time subscription for new PRs
    const channel = supabase
      .channel(`pr_notifications_${userEmail}_${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'personal_records_log',
          filter: `member_email=eq.${userEmail}`
        },
        (payload) => {
          // Reload notifications when a new PR is achieved
          loadUnnotifiedPRs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.email]);

  const dismissNotification = async (prId) => {
    try {
      // Add to dismissed set immediately for UI feedback
      setDismissed(prev => new Set(prev).add(prId));

      // Remove from notifications list with a small delay for fade animation
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== prId));
      }, 300);

      // Mark as notified in database
      await supabase
        .rpc('mark_prs_notified', { p_pr_ids: [prId] });
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const dismissAll = async () => {
    try {
      const allIds = notifications.map(n => n.id);

      if (allIds.length > 0) {
        await supabase
          .rpc('mark_prs_notified', { p_pr_ids: allIds });

        setNotifications([]);
      }
    } catch (error) {
      console.error('Error dismissing all notifications:', error);
    }
  };

  const getPRIcon = (prType) => {
    switch (prType) {
      case 'max_weight':
        return <Target className="w-5 h-5" />;
      case 'max_reps':
        return <TrendingUp className="w-5 h-5" />;
      case 'max_volume':
        return <Award className="w-5 h-5" />;
      case 'max_1rm':
        return <Trophy className="w-5 h-5" />;
      default:
        return <Trophy className="w-5 h-5" />;
    }
  };

  const getPRColor = (prType) => {
    switch (prType) {
      case 'max_weight':
        return 'from-red-500 to-orange-500';
      case 'max_reps':
        return 'from-blue-500 to-cyan-500';
      case 'max_volume':
        return 'from-green-500 to-emerald-500';
      case 'max_1rm':
        return 'from-purple-500 to-pink-500';
      default:
        return 'from-primary to-primary/80';
    }
  };

  const formatValue = (value, previousValue) => {
    if (previousValue && previousValue > 0) {
      const improvement = ((value - previousValue) / previousValue * 100).toFixed(1);
      return `+${improvement}% mejora`;
    }
    return 'Primer registro!';
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-sm">
      {/* Dismiss All Button */}
      {notifications.length > 1 && (
        <button
          onClick={dismissAll}
          className="w-full text-center text-sm text-gray-600 hover:text-gray-900 py-1 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm"
        >
          Marcar todas como leídas
        </button>
      )}

      {/* Notifications */}
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`bg-gradient-to-r ${getPRColor(notification.pr_type)} p-1 rounded-lg shadow-lg transition-all duration-300 ${
            dismissed.has(notification.id) ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0 animate-slide-in'
          }`}
          style={{
            animation: dismissed.has(notification.id) ? 'none' : 'slideIn 0.3s ease-out'
          }}
        >
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className={`p-2 rounded-lg bg-gradient-to-br ${getPRColor(notification.pr_type)} text-white flex-shrink-0`}>
                {getPRIcon(notification.pr_type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">
                      {notification.pr_title}
                    </h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {notification.exercise_name}
                    </p>
                  </div>
                  <button
                    onClick={() => dismissNotification(notification.id)}
                    className="text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full p-1 flex-shrink-0 transition-colors"
                    title="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="mt-2">
                  <p className="text-lg font-bold text-gray-900">
                    {notification.formatted_value}
                  </p>
                  <p className="text-xs text-green-600 font-medium">
                    {formatValue(notification.value, notification.previous_value)}
                  </p>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  {new Date(notification.achieved_at).toLocaleString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          .animate-slide-in {
            animation: slideIn 0.3s ease-out;
          }
        `
      }} />
    </div>
  );
};

export default PRNotifications;
